import React from 'react';
import { Loader2, Settings2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { getEmailSettings, getStripeSettings, StripeAdminSettings, EmailAdminSettings } from '../../api/adminSettingsApi';
import { StripeConfiguration } from '../../components/settings/admin/StripeConfiguration';
import { EmailConfiguration } from '../../components/settings/admin/EmailConfiguration';
import { IntegrationStatusCards } from '../../components/settings/admin/IntegrationStatusCards';
import { SecretUpdateModal } from '../../components/settings/admin/SecretUpdateModal';
import { SubscriptionCatalogSettings } from '../../components/settings/admin/SubscriptionCatalogSettings';

export const AdminSettings: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState<'stripe' | 'email' | 'tiers' | 'addons'>('stripe');
  const [stripeSettings, setStripeSettings] = React.useState<StripeAdminSettings | null>(null);
  const [emailSettings, setEmailSettings] = React.useState<EmailAdminSettings | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalTitle, setModalTitle] = React.useState('');
  const [modalFieldLabel, setModalFieldLabel] = React.useState('');
  const [savingSecret,] = React.useState(false);
  const [secretField, setSecretField] = React.useState<'secretKey' | 'webhookSecret' | 'clientSecret' | null>(null);

  const fetchSettings = React.useCallback(async () => {
    setLoading(true);
    try {
      const [stripe, email] = await Promise.all([getStripeSettings(), getEmailSettings()]);
      setStripeSettings(stripe);
      setEmailSettings(email);
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSecretUpdate = async (value: string) => {
    try {
        if (secretField === 'secretKey' && stripeSettings) {
            setStripeSettings({
                ...stripeSettings,
                secretKey: value,
            });
        }

        if (secretField === 'webhookSecret' && stripeSettings) {
            setStripeSettings({
                ...stripeSettings,
                webhookSecret: value,
            });
        }

        if (secretField === 'clientSecret' && emailSettings) {
            setEmailSettings({
                ...emailSettings,
                clientSecret: value,
            });
        }

        toast.success('Secret updated');

        setModalOpen(false);
    } catch {
        toast.error('Failed to update secret');
    }
  };

  const openModal = (title: string, fieldLabel: string) => {
    setModalTitle(title);
    setModalFieldLabel(fieldLabel);

    if (fieldLabel === 'Secret Key') {
        setSecretField('secretKey');
    } else if (fieldLabel === 'Webhook Secret') {
        setSecretField('webhookSecret');
    } else if (fieldLabel === 'Client Secret') {
        setSecretField('clientSecret');
    }

    setModalOpen(true);
  };

  return (
    <div className="w-full animate-fade-in space-y-6">
      <div className="rounded-2xl border border-strivre-lightnavy bg-white shadow-sm">
        <div className="border-b border-strivre-lightnavy bg-strivre-softgrey/50 p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-strivre-lightburgundy p-2 text-strivre-burgundy">
              <Settings2 size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 text-left">Admin Settings</h3>
              <p className="text-sm text-slate-500">Manage platform integrations and secure configuration.</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-strivre-burgundy">System Integration Status</h4>
            <div className="mt-4">
              <IntegrationStatusCards
                stripeConfigured={Boolean(stripeSettings?.publishableKey || stripeSettings?.secretKey || stripeSettings?.webhookSecret)}
                emailConfigured={Boolean(emailSettings?.tenantId || emailSettings?.clientId || emailSettings?.clientSecret)}
              />
            </div>
          </div>

          <div className="mb-6 flex flex-wrap gap-2 rounded-2xl border border-strivre-lightnavy bg-strivre-softgrey p-2">
            <button
              type="button"
              onClick={() => setActiveTab('stripe')}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition-all ${activeTab === 'stripe' ? 'bg-strivre-burgundy text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}
            >
              Stripe Configuration
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('email')}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition-all ${activeTab === 'email' ? 'bg-strivre-burgundy text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}
            >
              System Email Configuration
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('tiers')}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition-all ${activeTab === 'tiers' ? 'bg-strivre-burgundy text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}
            >
              Subscription Tiers
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('addons')}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition-all ${activeTab === 'addons' ? 'bg-strivre-burgundy text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}
            >
              Subscription Add-Ons
            </button>
          </div>

          {activeTab === 'tiers' || activeTab === 'addons' ? (
            <SubscriptionCatalogSettings mode={activeTab} />
          ) : loading ? (
            <div className="flex items-center justify-center rounded-2xl border border-strivre-lightnavy bg-strivre-softgrey p-8 text-slate-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin text-strivre-burgundy" />
              Loading admin settings...
            </div>
          ) : activeTab === 'stripe' ? (
            <StripeConfiguration initialValues={stripeSettings} loading={false} onRefresh={fetchSettings} onRequestSecretUpdate={openModal} />
          ) : (
            <EmailConfiguration initialValues={emailSettings} loading={false} onRefresh={fetchSettings} onRequestSecretUpdate={openModal} />
          )}
        </div>
      </div>

      <SecretUpdateModal
        isOpen={modalOpen}
        title={modalTitle}
        fieldLabel={modalFieldLabel}
        saving={savingSecret}
        onClose={() => setModalOpen(false)}
        onSave={handleSecretUpdate}
      />
    </div>
  );
};

export default AdminSettings;
